import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  alpha,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import axiosInstance from '../../../lib/axios';
import { RefundModal } from './RefundModal';

interface SaleItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface Sale {
  id: string;
  receiptNumber?: string;
  totalAmount: number;
  total?: number;
  subtotal?: number;
  tax?: number;
  discount?: number;
  paymentMethod: string;
  isRefunded: boolean;
  refundedAmount: number;
  refundedAt: string | null;
  items: SaleItem[];
  customer?: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface ReturnLookupModalProps {
  open: boolean;
  onClose: () => void;
  onRefundSuccess: () => void;
  colors: POSColors;
}

export const ReturnLookupModal: React.FC<ReturnLookupModalProps> = ({
  open,
  onClose,
  onRefundSuccess,
  colors,
}) => {
  const [receiptNumber, setReceiptNumber] = useState('');
  const [sale, setSale] = useState<Sale | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setReceiptNumber('');
      setSale(null);
      setError(null);
      setIsSearching(false);
      setRefundModalOpen(false);
    }
  }, [open]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-DZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSearch = async () => {
    if (!receiptNumber.trim()) {
      setError('Enter a receipt number to search');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSale(null);

    try {
      const response = await axiosInstance.get(`/sales/receipt/${receiptNumber.trim()}`);
      setSale(response.data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || 'Sale not found';
      setError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRefundSuccess = () => {
    setRefundModalOpen(false);
    onRefundSuccess();
    onClose();
  };

  // Normalize sale for RefundModal (handle both totalAmount and total)
  const normalizedSale = sale
    ? {
        ...sale,
        totalAmount: sale.totalAmount ?? sale.total ?? 0,
      }
    : null;

  return (
    <>
      <Dialog
        open={open && !refundModalOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: alpha(colors.accent, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UndoIcon sx={{ color: colors.accent, fontSize: 22 }} />
          </Box>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: colors.textPrimary,
            }}
          >
            Process Return
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* Search Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '13px',
                color: colors.textSecondary,
                mb: 1.5,
              }}
            >
              Enter or scan the receipt number to find the sale
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Receipt Number"
                placeholder="e.g., INV-001"
                value={receiptNumber}
                onChange={(e) => {
                  setReceiptNumber(e.target.value);
                  setError(null);
                }}
                onKeyPress={handleKeyPress}
                disabled={isSearching}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReceiptLongIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: colors.textMuted,
                    '&.Mui-focused': {
                      color: colors.accent,
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.surfaceLight,
                    '& fieldset': {
                      borderColor: colors.border,
                    },
                    '&:hover fieldset': {
                      borderColor: colors.textMuted,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.accent,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: colors.textPrimary,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isSearching}
                sx={{
                  minWidth: 100,
                  backgroundColor: colors.accent,
                  '&:hover': {
                    backgroundColor: colors.accentLight,
                  },
                }}
              >
                {isSearching ? (
                  <CircularProgress size={20} sx={{ color: '#FFF' }} />
                ) : (
                  <>
                    <SearchIcon sx={{ mr: 0.5 }} />
                    Search
                  </>
                )}
              </Button>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  backgroundColor: alpha('#EF4444', 0.1),
                  color: colors.textPrimary,
                  '& .MuiAlert-icon': {
                    color: '#EF4444',
                  },
                }}
              >
                {error}
              </Alert>
            )}
          </Box>

          {/* Sale Preview */}
          {sale && (
            <Box
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              {/* Sale Header */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: alpha(colors.accent, 0.05),
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontWeight: 600,
                      }}
                    >
                      Receipt
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: colors.textPrimary,
                        fontFamily: 'monospace',
                      }}
                    >
                      {sale.receiptNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontWeight: 600,
                      }}
                    >
                      Total
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: colors.accent,
                        fontFamily: 'monospace',
                      }}
                    >
                      {formatPrice(sale.totalAmount ?? sale.total ?? 0)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                    <Typography sx={{ fontSize: '13px', color: colors.textSecondary }}>
                      {sale.customer
                        ? `${sale.customer.firstName} ${sale.customer.lastName}`
                        : 'Walk-in Customer'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: colors.textMuted }}>
                    {formatDate(sale.createdAt)}
                  </Typography>
                </Box>
              </Box>

              {/* Already Refunded Warning */}
              {sale.isRefunded && (
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 0,
                    backgroundColor: alpha('#F59E0B', 0.1),
                    color: colors.textPrimary,
                    '& .MuiAlert-icon': {
                      color: '#F59E0B',
                    },
                  }}
                >
                  This sale has already been fully refunded ({formatPrice(sale.refundedAmount)})
                </Alert>
              )}

              {/* Partial Refund Notice */}
              {!sale.isRefunded && sale.refundedAmount > 0 && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 0,
                    backgroundColor: alpha(colors.accent, 0.1),
                    color: colors.textPrimary,
                    '& .MuiAlert-icon': {
                      color: colors.accent,
                    },
                  }}
                >
                  Partial refund applied: {formatPrice(sale.refundedAmount)}
                </Alert>
              )}

              {/* Items List */}
              <Box sx={{ p: 2 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Items ({sale.items.length})
                </Typography>

                {sale.items.map((item, index) => (
                  <Box key={item.id}>
                    {index > 0 && <Divider sx={{ my: 1, borderColor: colors.border }} />}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: colors.textPrimary,
                          }}
                        >
                          {item.productName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: colors.textMuted,
                          }}
                        >
                          {formatPrice(item.unitPrice)} × {item.quantity}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: colors.textPrimary,
                          fontFamily: 'monospace',
                        }}
                      >
                        {formatPrice(item.subtotal)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Refund Action */}
              {!sale.isRefunded && (
                <Box
                  sx={{
                    p: 2,
                    borderTop: `1px solid ${colors.border}`,
                    backgroundColor: alpha(colors.accent, 0.03),
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    startIcon={<UndoIcon />}
                    onClick={() => setRefundModalOpen(true)}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    Process Refund
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: colors.border,
              color: colors.textSecondary,
              '&:hover': {
                borderColor: colors.textMuted,
                backgroundColor: alpha(colors.textMuted, 0.1),
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* RefundModal */}
      {normalizedSale && (
        <RefundModal
          open={refundModalOpen}
          sale={normalizedSale}
          onClose={() => setRefundModalOpen(false)}
          onSuccess={handleRefundSuccess}
        />
      )}
    </>
  );
};

export default ReturnLookupModal;
